import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Button } from '@/components/ui/button';
import { TableCell, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  Building2Icon,
  ChevronDownIcon,
  GitBranchIcon,
  RotateCcwIcon,
  SaveIcon,
  Undo2Icon,
} from 'lucide-react';

import { useSaveTenantCustomerIdentity } from '../api/tenant-logo';
import { CustomerIdentityColorCell } from './customer-identity-color-cell';
import { CustomerIdentityLogoCell } from './customer-identity-logo-cell';
import type {
  CustomerIdentityPreview,
  CustomerIdentitySubTenant,
  CustomerIdentityTenant,
} from './types/types';
import {
  createCustomerIdentityDraft,
  createCustomerIdentityPreview,
  createCustomerIdentityUpdatePayload,
  isCustomerIdentityDraftDirty,
} from './utils/customer-identity';

type CustomerIdentityTableRowProps = {
  tenant: CustomerIdentityTenant | CustomerIdentitySubTenant;
  parentTenantName?: string;
  subTenantCount?: number;
  isExpanded?: boolean;
  isSelected?: boolean;
  onPreviewChange?: (preview: CustomerIdentityPreview) => void;
  onToggle?: () => void;
};

export const CustomerIdentityTableRow = ({
  tenant,
  parentTenantName,
  subTenantCount = 0,
  isExpanded = false,
  isSelected = false,
  onPreviewChange,
  onToggle,
}: CustomerIdentityTableRowProps) => {
  const { t } = useTranslation();
  const { isPending: isSavingIdentity, mutateAsync: saveTenantCustomerIdentity } =
    useSaveTenantCustomerIdentity();
  const [baseline, setBaseline] = useState(() => createCustomerIdentityDraft(tenant));
  const [draft, setDraft] = useState(() => createCustomerIdentityDraft(tenant));
  const previousTenantIdRef = useRef(tenant.id);
  const hasSubTenants = subTenantCount > 0;
  const isPending = isSavingIdentity;
  const canResetToDefaults =
    draft.primaryColor !== null ||
    draft.secondaryColor !== null ||
    draft.logoFile !== null ||
    draft.logoId !== null;

  useEffect(() => {
    const nextBaseline = createCustomerIdentityDraft(tenant);
    const previousTenantId = previousTenantIdRef.current;

    setBaseline((currentBaseline) => {
      setDraft((currentDraft) =>
        previousTenantId === tenant.id &&
        isCustomerIdentityDraftDirty(currentDraft, currentBaseline)
          ? currentDraft
          : nextBaseline
      );

      return nextBaseline;
    });

    previousTenantIdRef.current = tenant.id;
  }, [tenant.id, tenant.colors?.primaryColor, tenant.colors?.secondaryColor, tenant.logoId]);

  useEffect(() => {
    const previewUrl = draft.logoPreviewUrl;

    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [draft.logoPreviewUrl]);

  useEffect(() => {
    if (isSelected) {
      onPreviewChange?.(createCustomerIdentityPreview(tenant, draft));
    }
  }, [draft, isSelected, onPreviewChange, tenant]);

  const isDirty = isCustomerIdentityDraftDirty(draft, baseline);

  const handleRowClick = () => {
    onPreviewChange?.(createCustomerIdentityPreview(tenant, draft));
  };

  const handleLogoUpload = (file: File) => {
    const logoPreviewUrl = URL.createObjectURL(file);

    setDraft((currentDraft) => ({
      ...currentDraft,
      logoId: null,
      logoFile: file,
      logoPreviewUrl,
    }));
  };

  const handleLogoRemove = () => {
    setDraft((currentDraft) => ({
      ...currentDraft,
      logoId: null,
      logoFile: null,
      logoPreviewUrl: null,
    }));
  };

  const resetDraft = () => {
    setDraft(baseline);
  };

  const resetDraftToDefaults = () => {
    setDraft({
      primaryColor: null,
      secondaryColor: null,
      logoId: null,
      logoFile: null,
      logoPreviewUrl: null,
    });
  };

  const handleApply = async () => {
    const payload = createCustomerIdentityUpdatePayload(draft, baseline);

    let savedIdentity: Awaited<ReturnType<typeof saveTenantCustomerIdentity>>;

    try {
      savedIdentity = await saveTenantCustomerIdentity({
        ...payload,
        tenantId: tenant.id,
      });
    } catch {
      toast.error(t('customerIdentitySaveFailed'));
      return;
    }

    const savedDraft = {
      ...draft,
      logoId: savedIdentity.logoId ?? draft.logoId,
      logoFile: null,
      logoPreviewUrl: null,
    };

    setBaseline(savedDraft);
    setDraft(savedDraft);
    toast.success(t('customerIdentitySaveSuccess'));
  };

  return (
    <TableRow
      aria-selected={isSelected}
      className={cn(
        'h-[72px] cursor-pointer border-b border-background-darker bg-white hover:bg-background-lighter/30',
        isSelected &&
          'bg-primary-light/10 shadow-[inset_4px_0_0_hsl(var(--primary-color-light))] hover:bg-primary-light/10 [&>td]:border-y [&>td]:border-primary-light/30'
      )}
      onClick={handleRowClick}
    >
      <TableCell className="min-w-0 px-2 py-3 align-middle first:pl-3 2xl:px-4 2xl:first:pl-5">
        <TenantNameCell
          tenantName={tenant.name}
          parentTenantName={parentTenantName}
          subTenantCount={subTenantCount}
          isExpanded={isExpanded}
          hasSubTenants={hasSubTenants}
          onToggle={onToggle}
        />
      </TableCell>
      <TableCell className="min-w-0 px-1 py-3 align-middle 2xl:px-4">
        <CustomerIdentityColorCell
          value={draft.primaryColor}
          fallbackCssVariable="--primary-color-light"
          onChange={(primaryColor) =>
            setDraft((currentDraft) => ({ ...currentDraft, primaryColor }))
          }
        />
      </TableCell>
      <TableCell className="min-w-0 px-1 py-3 align-middle 2xl:px-4">
        <CustomerIdentityColorCell
          value={draft.secondaryColor}
          fallbackCssVariable="--primary-color"
          onChange={(secondaryColor) =>
            setDraft((currentDraft) => ({ ...currentDraft, secondaryColor }))
          }
        />
      </TableCell>
      <TableCell className="min-w-0 py-3 pl-4 pr-2 align-middle 2xl:px-4">
        <CustomerIdentityLogoCell
          logoId={draft.logoId}
          logoPreviewUrl={draft.logoPreviewUrl}
          tenantName={tenant.name}
          onUpload={handleLogoUpload}
          onRemove={handleLogoRemove}
          isUploading={isSavingIdentity}
        />
      </TableCell>
      <TableCell className="min-w-0 px-2 py-3 text-right align-middle last:pr-3 2xl:px-4 2xl:last:pr-5">
        <ActionsCell
          canResetToDefaults={canResetToDefaults}
          isDirty={isDirty}
          isPending={isPending}
          onApply={handleApply}
          onReset={resetDraft}
          onResetToDefaults={resetDraftToDefaults}
        />
      </TableCell>
    </TableRow>
  );
};

type TenantNameCellProps = {
  tenantName: string;
  parentTenantName?: string;
  subTenantCount: number;
  isExpanded: boolean;
  hasSubTenants: boolean;
  onToggle?: () => void;
};

const TenantNameCell = ({
  tenantName,
  parentTenantName,
  subTenantCount,
  isExpanded,
  hasSubTenants,
  onToggle,
}: TenantNameCellProps) => {
  const { t } = useTranslation();

  if (parentTenantName) {
    return (
      <div className="ml-6 flex min-w-0 flex-col gap-1">
        <span className="text-xs font-semibold leading-5 text-text-secondary">
          {parentTenantName} /
        </span>
        <span className="flex min-w-0 items-center gap-2">
          <GitBranchIcon aria-hidden="true" className="size-4 shrink-0 text-text-secondary" />
          <span className="truncate text-xs font-semibold leading-5 text-primary-darker">
            {tenantName}
          </span>
        </span>
      </div>
    );
  }

  return (
    <div className="flex min-w-0 items-center gap-2">
      {hasSubTenants ? (
        <button
          type="button"
          aria-expanded={isExpanded}
          aria-label={t(isExpanded ? 'collapseTenant' : 'expandTenant', { tenantName })}
          className="flex size-5 shrink-0 items-center justify-center text-text-secondary"
          onClick={onToggle}
        >
          <ChevronDownIcon
            aria-hidden="true"
            className={cn('size-4 transition-transform', !isExpanded && '-rotate-90')}
          />
        </button>
      ) : (
        <span className="size-5 shrink-0" />
      )}

      <Building2Icon aria-hidden="true" className="size-4 shrink-0 text-text-secondary" />
      <span className="min-w-0 truncate text-xs font-semibold leading-5 text-primary-darker">
        {tenantName}
      </span>

      {hasSubTenants ? (
        <span className="ml-1 flex size-8 shrink-0 items-center justify-center rounded-full border border-background-darker bg-white text-xs font-semibold text-text-secondary">
          {subTenantCount}
        </span>
      ) : null}
    </div>
  );
};

type ActionsCellProps = {
  canResetToDefaults: boolean;
  isDirty: boolean;
  isPending: boolean;
  onApply: () => Promise<void>;
  onReset: () => void;
  onResetToDefaults: () => void;
};

const ActionsCell = ({
  canResetToDefaults,
  isDirty,
  isPending,
  onApply,
  onReset,
  onResetToDefaults,
}: ActionsCellProps) => {
  const { t } = useTranslation();
  const isApplyDisabled = !isDirty || isPending;

  return (
    <div className="flex min-w-[5.75rem] items-center justify-end gap-1 whitespace-nowrap 2xl:gap-2">
      {canResetToDefaults ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label={t('resetToDefaults')}
              className="flex size-8 items-center justify-center rounded-sm text-text-secondary transition-colors hover:bg-primary-light hover:text-primary-darker"
              disabled={isPending}
              onClick={onResetToDefaults}
            >
              <RotateCcwIcon aria-hidden="true" className="size-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent
            side="left"
            className="whitespace-nowrap border border-background-darker bg-white px-3 py-2 text-xs font-medium leading-5 text-primary-darker shadow-md"
          >
            {t('resetToDefaults')}
          </TooltipContent>
        </Tooltip>
      ) : null}
      {isDirty ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label={t('discardChanges')}
              className="flex size-8 items-center justify-center rounded-sm text-text-secondary transition-colors hover:bg-primary-light hover:text-primary-darker"
              disabled={isPending}
              onClick={onReset}
            >
              <Undo2Icon aria-hidden="true" className="size-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent
            side="left"
            className="whitespace-nowrap border border-background-darker bg-white px-3 py-2 text-xs font-medium leading-5 text-primary-darker shadow-md"
          >
            {t('discardChanges')}
          </TooltipContent>
        </Tooltip>
      ) : null}
      <Button
        aria-label={t('apply')}
        variant="outline"
        size="sm"
        disabled={isApplyDisabled}
        className={cn(
          'h-8 gap-1.5 rounded-sm border-background-darker px-2 text-xs font-semibold 2xl:px-3',
          !isApplyDisabled &&
            'border-primary-light bg-primary-light text-white hover:bg-primary hover:text-white'
        )}
        onClick={onApply}
      >
        <SaveIcon aria-hidden="true" className="size-4" />
        <span className="hidden 2xl:inline">{t('apply')}</span>
      </Button>
    </div>
  );
};
